import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeAnalytics, updateAnalyticsConsent } from '../services/consent';

// Declare global types for Civic Cookie Control
declare global {
    interface Window {
        CookieControl?: any;
        gtag?: (...args: any[]) => void;
    }
}

// Define the types of storage we need consent for
export type ConsentType = 'essential' | 'analytics' | 'preferences';

// Define the consent state interface
export interface ConsentState {
    essential: boolean; // Required for app functionality (tokens, auth)
    analytics: boolean; // Analytics tracking
    preferences: boolean; // User preferences storage
    consentGiven: boolean; // Whether the user has made any choice at all
}

// Define the context interface
interface ConsentContextType {
    consent: ConsentState;
    updateConsent: (type: ConsentType, value: boolean) => void;
    acceptAll: () => void;
    rejectAll: () => void;
    hasConsented: () => boolean;
}

// Create the context with default values
const ConsentContext = createContext<ConsentContextType>({
    consent: {
        essential: false,
        analytics: false,
        preferences: false,
        consentGiven: false
    },
    updateConsent: () => { },
    acceptAll: () => { },
    rejectAll: () => { },
    hasConsented: () => false
});

// Storage key for persisting consent
const CONSENT_STORAGE_KEY = 'speechbox_cookie_consent';

// Provider component
export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Initialize state from localStorage if available
    const [consent, setConsent] = useState<ConsentState>(() => {
        try {
            const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
            if (storedConsent) {
                return JSON.parse(storedConsent);
            }
        } catch (error) {
            console.error('Error reading consent from localStorage', error);
        }

        return {
            essential: true, // Essential is always true for app functionality
            analytics: false,
            preferences: false,
            consentGiven: false
        };
    });

    // Listen for Civic Cookie Control events
    useEffect(() => {
        const handleCivicConsentUpdate = (event: CustomEvent) => {
            const newConsent = event.detail;
            setConsent(prev => ({
                ...prev,
                ...newConsent
            }));
        };

        window.addEventListener('civicConsentUpdate', handleCivicConsentUpdate as EventListener);

        return () => {
            window.removeEventListener('civicConsentUpdate', handleCivicConsentUpdate as EventListener);
        };
    }, []);

    // Update localStorage when consent changes
    useEffect(() => {
        try {
            localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));

            // Initialize or update analytics based on consent
            if (consent.consentGiven) {
                if (consent.analytics) {
                    initializeAnalytics(consent);
                } else {
                    updateAnalyticsConsent(consent);
                }
            }
        } catch (error) {
            console.error('Error saving consent to localStorage', error);
        }
    }, [consent]);

    // Update a specific consent type
    const updateConsent = (type: ConsentType, value: boolean) => {
        setConsent(prev => ({
            ...prev,
            [type]: value,
            consentGiven: true
        }));
    };

    // Accept all consent types
    const acceptAll = () => {
        setConsent({
            essential: true,
            analytics: true,
            preferences: true,
            consentGiven: true
        });
    };

    // Reject non-essential consent types
    const rejectAll = () => {
        setConsent({
            essential: true, // Essential is always true as it's required for app functionality
            analytics: false,
            preferences: false,
            consentGiven: true
        });
    };

    // Check if user has provided any consent choice
    const hasConsented = () => {
        return consent.consentGiven;
    };

    return (
        <ConsentContext.Provider value={{ consent, updateConsent, acceptAll, rejectAll, hasConsented }}>
            {children}
        </ConsentContext.Provider>
    );
};

// Custom hook for using the consent context
export const useConsent = () => useContext(ConsentContext);

export default ConsentContext;
